import { useEffect, useRef } from "react";
import { useMap } from "react-map-gl/maplibre";
import { LngLatLike } from "react-map-gl/maplibre";

/**
 * Locks the map center to a point while allowing zoom/rotate.
 *
 * @param targetPoint - The point to lock the map center to, or null to disable.
 * @param verticalOffsetPx - Shift the locked center upward so that `targetPoint`
 *   appears this many pixels below the viewport center. Pass a positive value to
 *   give a popup that opens upward more room at the top of the screen.
 */
export function useMapCenterLock(targetPoint: LngLatLike | null, verticalOffsetPx = 0): void {
  const { current: map } = useMap();
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!map || !targetPoint) return;

    // scrollZoom lives on the underlying MapLibre instance
    const scrollZoom = map.getMap().scrollZoom;

    // Zoom around the map center instead of the cursor — no center drift on scroll zoom
    scrollZoom.disable();
    scrollZoom.enable({ around: "center" });

    /**
     * Compute the geographic center that places `targetPoint` `verticalOffsetPx`
     * pixels below the viewport center. When offset is 0 this is just targetPoint.
     *
     * By centering the map on this shifted point, the popup anchor (targetPoint)
     * moves down on screen, giving a popup that opens upward more room above it.
     */
    const getLockedCenter = (): LngLatLike => {
      if (verticalOffsetPx === 0) return targetPoint;
      const { x, y } = map.project(targetPoint);
      return map.unproject([x, y - verticalOffsetPx]);
    };

    const onDragStart = () => {
      isDraggingRef.current = true;
    };
    const onMove = () => {
      if (isDraggingRef.current) map.setCenter(getLockedCenter());
    };
    const onDragEnd = () => {
      isDraggingRef.current = false;
    };

    map.flyTo({ center: getLockedCenter(), maxDuration: 1000 });

    map.on("dragstart", onDragStart);
    map.on("move", onMove);
    map.on("dragend", onDragEnd);

    return () => {
      map.off("dragstart", onDragStart);
      map.off("move", onMove);
      map.off("dragend", onDragEnd);
      // Restore default scroll zoom behavior
      scrollZoom.disable();
      scrollZoom.enable();
    };
  }, [targetPoint, map, verticalOffsetPx]);
}
